/**
 * cdnImageUrl / cdnImageSrcSet
 *
 * Pure, server-safe helpers that apply Cloudflare Image Resizing to
 * STABLE R2 URLs only (https://cdn.ballee.co/<key>). Everything else —
 * Supabase signed URLs (rotating 1h token), legacy S3, data: URIs,
 * non-http strings — passes through UNCHANGED.
 *
 * Why R2-only: Cloudflare Images caches the transform by URL. A signed
 * Supabase URL bakes a token that rotates hourly, so transforming it
 * would mint a never-cacheable key and break the image when the token
 * expires (Constraint 1, video-architecture lock).
 *
 * These helpers read R2_PUBLIC_URL at module-eval / call time (server
 * env, NOT NEXT_PUBLIC_). They are safe to call from Server Components.
 * In dev or when R2_PUBLIC_URL is unset, transform is skipped and the
 * original URL is returned (graceful degradation).
 *
 * @see ./image-transform.service.ts (ImageTransformService, ImageVariants)
 */
import {
  ImageTransformService,
  type ImageTransformOptions,
  type ImageVariantKey,
} from './image-transform.service';

const R2_HOST = 'cdn.ballee.co';

/** Lazily build a transform service from env. Null if unconfigured. */
let _cached: ImageTransformService | null | undefined;
function getTransformService(): ImageTransformService | null {
  if (_cached !== undefined) return _cached;
  const origin = process.env.R2_PUBLIC_URL;
  _cached = origin ? new ImageTransformService({ proxyOrigin: origin }) : null;
  return _cached;
}

/** True only for stable R2 cdn.ballee.co URLs. */
export function isR2StableUrl(src: string | null | undefined): boolean {
  return !!src && src.startsWith(`https://${R2_HOST}/`);
}

/**
 * Strip the cdn.ballee.co origin so ImageTransformService builds a clean
 * `…/cdn-cgi/image/<opts>/<key>` instead of nesting a full URL.
 */
function r2KeyOf(url: string): string {
  return url.slice(`https://${R2_HOST}/`.length);
}

/**
 * Apply a named variant transform to an R2 URL. Pass-through for
 * Supabase-signed / legacy / non-http inputs.
 */
export function cdnImageUrl(
  src: string | null | undefined,
  variant: ImageVariantKey,
): string | null {
  if (!src) return null;
  if (!isR2StableUrl(src)) return src; // Supabase-signed, legacy, data: → untouched
  const svc = getTransformService();
  if (!svc) return src; // dev / unconfigured → original
  return svc.url(r2KeyOf(src), variant);
}

/**
 * Build a tiny, heavily-blurred LQIP placeholder URL for blur-up loading.
 *
 * Returns null for null/empty input and for non-R2 sources (Supabase-signed
 * URLs carry a rotating token and aren't transformable, so blur-up only
 * applies to stable R2 URLs). Also returns null in dev / when R2_PUBLIC_URL
 * is unset (no transform service), matching `cdnImageUrl`'s degradation.
 *
 * `https://cdn.ballee.co/x.jpg`
 *   → `…/cdn-cgi/image/width=16,quality=30,blur=250/x.jpg`
 */
export function cdnImagePlaceholder(
  src: string | null | undefined,
): string | null {
  if (!isR2StableUrl(src)) return null; // null/empty + Supabase-signed/legacy
  const svc = getTransformService();
  if (!svc) return null; // dev / unconfigured
  return svc.url(r2KeyOf(src!), 'PLACEHOLDER');
}

/**
 * Build a single-width transform URL with explicit options, matching the
 * shape `cdnImageSrcSet` emits. Use this for the `src` fallback so it
 * REUSES one of the srcset widths rather than minting a distinct
 * aspect-locked variant (e.g. a 200×200 `THUMBNAIL`): a separate `src`
 * variant is a separate Cloudflare billable transformation, and crawlers
 * — which ignore `srcset` and fetch `src` — hit it on every public
 * profile. Reusing a srcset width keeps the per-photo unique-transform
 * count down. Pass-through for non-R2 inputs.
 */
export function cdnImageWidthUrl(
  src: string | null | undefined,
  width: number,
  baseOptions: ImageTransformOptions,
): string | null {
  if (!src) return null;
  if (!isR2StableUrl(src)) return src;
  const svc = getTransformService();
  if (!svc) return src;
  return svc.customUrl(r2KeyOf(src), { ...baseOptions, width });
}

/**
 * Build a responsive `srcset` string for an R2 URL at the given widths.
 * Pass-through (returns null → caller omits srcset) for non-R2 inputs,
 * so the plain `src` still carries the browser's only candidate.
 */
export function cdnImageSrcSet(
  src: string | null | undefined,
  widths: number[],
  baseOptions: ImageTransformOptions,
): string | null {
  if (!isR2StableUrl(src)) return null;
  const svc = getTransformService();
  if (!svc) return null;
  const key = r2KeyOf(src!);
  return widths
    .map((w) => `${svc.customUrl(key, { ...baseOptions, width: w })} ${w}w`)
    .join(', ');
}

/** The public-dancer-page render slots a photo can occupy. */
export type DancerImageSlot = 'HERO' | 'GALLERY_LEAD' | 'GALLERY_TILE';

export interface DancerImageSlotSpec {
  /** The width baked into `src` — ALWAYS one of `srcSetWidths` (crawlers
   * ignore `srcset` and fetch `src`; a distinct width would be a distinct
   * billable Cloudflare transform). */
  srcWidth: number;
  /** The responsive candidate widths, clean 2× steps. */
  srcSetWidths: number[];
  /** Fit/quality shared by `src` and every `srcset` candidate. */
  options: ImageTransformOptions;
}

/**
 * THE dancer-photo transform registry — one composer for the page AND the
 * image sitemap.
 *
 * Before this, the page (`dancer-hero.tsx`, `dancer-gallery.tsx`) and the
 * sitemap (`lib/seo/dancer-image-sitemap.ts`) each picked their own transform:
 * the page emitted width-only `scale-down`/`contain` variants while the
 * sitemap emitted the aspect-locked 800×800 `ImageVariants.GALLERY`. Neither
 * side was wrong on its own; together they doubled the billable transform
 * count for every sitemapped photo and guaranteed the sitemap advertised a URL
 * the page never rendered. Cloudflare bills per UNIQUE transformation and
 * Ballee exhausted the free tier on 2026-06-06 — see the COST MODEL note in
 * `image-transform.service.ts`. Adding a 12th `ImageVariants` entry would have
 * made that worse, so the slots below reuse widths the page already mints.
 *
 * KEEP IN SYNC is not a thing here by construction: both surfaces read this
 * table, so a change to a width or quality moves them together.
 */
export const DancerImageSlots: Record<DancerImageSlot, DancerImageSlotSpec> = {
  /** Hero portrait — `dancer-hero.tsx`. `contain` shows the print whole. */
  HERO: {
    srcWidth: 1200,
    srcSetWidths: [600, 1200, 1600],
    options: { fit: 'contain', quality: 85 },
  },
  /** First tile of the first non-empty gallery section (4:3 lead). */
  GALLERY_LEAD: {
    srcWidth: 800,
    srcSetWidths: [400, 800, 1200],
    options: { fit: 'scale-down', quality: 80 },
  },
  /**
   * Every other gallery tile (4:5 portrait) — and the slot the image sitemap
   * emits for gallery photos, because all but one photo per dancer renders
   * here. `scale-down` (not `cover`) because these are width-only transforms:
   * Cloudflare's `cover` needs BOTH dimensions and falls back when given one.
   */
  GALLERY_TILE: {
    srcWidth: 400,
    srcSetWidths: [200, 400, 800],
    options: { fit: 'scale-down', quality: 75 },
  },
};

/**
 * The `src` a dancer photo renders at in the given slot. Pass-through for
 * non-R2 inputs (Supabase cover-proxy URLs, legacy Meteor S3, signed URLs),
 * so page and sitemap agree on those too — by both leaving them alone.
 */
export function dancerImageSrc(
  src: string | null | undefined,
  slot: DancerImageSlot,
): string | null {
  const spec = DancerImageSlots[slot];
  return cdnImageWidthUrl(src, spec.srcWidth, spec.options);
}

/** The responsive `srcset` for a dancer photo in the given slot. */
export function dancerImageSrcSet(
  src: string | null | undefined,
  slot: DancerImageSlot,
): string | null {
  const spec = DancerImageSlots[slot];
  return cdnImageSrcSet(src, spec.srcSetWidths, spec.options);
}
